import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Video, Loader2, Play, VideoOff } from 'lucide-react';
import { getAppointmentTranscript } from '../redux/appointmentSlice';
import { getSocket } from '../socket';
import RecordingModal from './RecordingModal';

// Meeting RECORDING card: lists this appointment's recordings (max two), each
// with its own View button (full-screen player + synced transcript modal).
// Per-recording states: pending → "Generating transcription…", done → View,
// skipped (video only) / failed → View without transcript. Renders nothing when
// there are no recordings; polls silently while one is expected or pending.
export default function MeetingSummary({ appointmentId, expectRecording = false, className = '' }) {
    const { t } = useTranslation(['appointmentdetails']);
    const dispatch = useDispatch();
    const [state, setState] = useState({ loading: true, recordings: [] });
    const [openIdx, setOpenIdx] = useState(null);
    const [tick, setTick] = useState(0); // bumped by the recording:ready socket → refetch

    // Live push from the backend the moment a recording is registered / its
    // transcript lands. The socket may not be connected yet, so retry attaching.
    useEffect(() => {
        if (!appointmentId) return undefined;
        let alive = true;
        let retryTimer = null;
        let attachedSocket = null;
        const onReady = (p) => { if (p && p.appointmentId === appointmentId) setTick((v) => v + 1); };
        const attach = () => {
            if (!alive) return;
            const socket = getSocket && getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attachedSocket = socket;
            socket.on('recording:ready', onReady);
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attachedSocket) attachedSocket.off('recording:ready', onReady);
        };
    }, [appointmentId]);

    useEffect(() => {
        let alive = true;
        let timer = null;
        if (!appointmentId) { setState({ loading: false, recordings: [] }); return; }

        const schedule = (ms) => { timer = setTimeout(() => fetchOnce(false), ms); };
        const fetchOnce = (isFirst) => {
            if (isFirst) setState((s) => ({ ...s, loading: true }));
            dispatch(getAppointmentTranscript(appointmentId))
                .unwrap()
                .then((data) => {
                    if (!alive) return;
                    // New shape: data.recordings[]; fallback: wrap the single row.
                    const recs = (data && Array.isArray(data.recordings) && data.recordings.length)
                        ? data.recordings
                        : (data && data.recordingKey ? [data] : []);
                    setState({ loading: false, recordings: recs });
                    const anyPending = recs.some((r) => (r.transcriptStatus || (r.transcript ? 'done' : 'pending')) === 'pending');
                    if (anyPending) schedule(6000);           // transcript still generating
                    else if (!recs.length && expectRecording) schedule(8000); // watch for a recording
                })
                .catch(() => {
                    if (!alive) return;
                    setState((s) => ({ loading: false, recordings: s.recordings }));
                    if (expectRecording) schedule(10000);
                });
        };
        fetchOnce(true);
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [appointmentId, dispatch, expectRecording, tick]);

    const { recordings } = state;

    // No recording → render nothing (any watching happens silently).
    if (!recordings.length) return null;

    const statusOf = (r) => r.transcriptStatus || (r.transcript && r.transcript.trim() ? 'done' : 'pending');
    const openRec = openIdx != null ? recordings[openIdx] : null;

    return (
        <div data-testid="meeting-recording" className={`rounded-2xl border border-[#4ABDBD]/20 bg-gradient-to-br from-[#4ABDBD]/5 to-transparent p-4 sm:p-5 ${className}`}>
            <div className="flex items-center gap-2 mb-3">
                <Video className="w-4 h-4 text-[#3AA8A8]" />
                <h4 className="text-sm sm:text-base font-bold text-[#2D8A8A]">
                    {t('appointmentdetails.meeting_recording', 'Meeting recording')}
                </h4>
            </div>

            <div className="space-y-3">
                {recordings.map((rec, idx) => {
                    const st = statusOf(rec);
                    return (
                        <div key={rec.id || idx} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`meeting-recording-item-${idx}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-16 h-12 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                                    <Video className="w-5 h-5 text-white/70" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">
                                        {recordings.length > 1
                                            ? `${t('appointmentdetails.recording', 'Recording')} ${idx + 1}`
                                            : t('appointmentdetails.recording_available', 'This video is recorded in the meeting.')}
                                    </p>
                                    {st === 'skipped' && (
                                        <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <VideoOff className="w-3 h-3" /> {t('appointmentdetails.video_only', 'Video only — no transcription')}
                                        </p>
                                    )}
                                    {st === 'failed' && (
                                        <p className="text-xs text-amber-500">
                                            {t('appointmentdetails.transcript_unavailable', 'Transcript unavailable')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {st === 'pending' ? (
                                <div data-testid="meeting-recording-generating" className="flex items-center gap-2 text-sm text-amber-600">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('appointmentdetails.generating_transcript', 'Generating transcription…')}
                                </div>
                            ) : rec.recordingUrl ? (
                                <button
                                    type="button"
                                    data-testid={`meeting-recording-view-${idx}`}
                                    onClick={() => setOpenIdx(idx)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg"
                                >
                                    <Play className="w-4 h-4" /> {t('appointmentdetails.view', 'View')}
                                </button>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <RecordingModal
                open={openIdx != null}
                onClose={() => setOpenIdx(null)}
                src={openRec?.recordingUrl}
                transcript={openRec?.transcript?.trim()}
                segments={openRec?.timedTranscript}
            />
        </div>
    );
}
